import React, { useState, useEffect, useRef } from 'react';
import { Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import filesApis, { UploadOptions, UploadReturn } from './filesApis';
import fetchApi from '../../../utils/api/fetchApi';
import antdMessage from '../system/antdMessage';

/** 上传文件的数据结构 */
export interface UploadFileType {
    /** 文件在线地址 */
    fileUrl: string;
    /** 文件类型 */
    type?: string;
    /** 文件大小 */
    size: number;
    /** 唯一标识 */
    uid: string;
    /** 文件名 */
    name: string;
    /** 文件服务器返回的文件 id */
    fileId: string;
}

interface FileUploadProps {
    /** 上传参数（callback 由组件内部处理，无需外部传入） */
    uploadOptions: Omit<UploadOptions, 'callback'>;
    /** 受控的文件列表 */
    value?: UploadFileType[];
    /** 文件列表变化回调 */
    onChange?: (fileList: UploadFileType[]) => void;
    /** 单个文件上传成功后的回调 */
    onUploadSuccess?: (file: UploadFileType) => void;
    /** 按钮文案 */
    buttonText?: string;
    /** 是否显示已上传文件列表，默认 false */
    showUploadList?: boolean;
    /** 是否禁用上传按钮，默认 false */
    disabled?: boolean;
    /** 其他可透传给 antd Upload 的属性 */
    [key: string]: any;
}

const FileUpload: React.FC<FileUploadProps> = ({
    uploadOptions,
    value,
    onChange,
    onUploadSuccess,
    buttonText = '附件',
    showUploadList = false,
    disabled = false,
    ...restProps
}) => {
    // 内部状态：当外部未受控时使用
    const [innerFileList, setInnerFileList] = useState<UploadFileType[]>([]);
    // 判断是否受控：外部同时传入了 value 和 onChange 才算受控
    const isControlled = value !== undefined && onChange !== undefined;
    const fileList = isControlled ? value! : innerFileList;

    const [uploadReturn, setUploadReturn] = useState<UploadReturn | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 保存回调引用，避免闭包过期
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onUploadSuccessRef = useRef(onUploadSuccess);
    onUploadSuccessRef.current = onUploadSuccess;

    // 当关键上传参数变化时，重新获取上传配置
    useEffect(() => {
        let isMounted = true;
        const fetchConfig = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await filesApis.upload({
                    ...uploadOptions,
                    callback: undefined as any, // 内部自行处理上传结果
                });
                if (!isMounted) return;
                if (res.success && res.data) {
                    setUploadReturn(res.data);
                } else {
                    setError(res.message || '获取上传配置失败');
                }
            } catch (e: any) {
                if (isMounted) {
                    setError(e.message || '获取上传配置异常');
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchConfig();
        return () => {
            isMounted = false;
        };
    }, [
        uploadOptions.serverUrl,
        uploadOptions.filesServerId,
        uploadOptions.serviceId,
        uploadOptions.filesGroupId,
        uploadOptions.subdirectory,
        uploadOptions.fileValidityPeriodId,
        JSON.stringify(uploadOptions.extendedData),
    ]);

    // 移除 config 中自带的 onChange，避免冲突
    const config = uploadReturn?.config;
    const { onChange: _configOnChange, ...restConfig } = config || {};

    // 处理文件状态变化，将上传成功的文件转换为 UploadFileType 格式
    const handleChange = (info: any) => {
        let newFileList = [...info.fileList];

        // 处理上传完成的文件
        newFileList = newFileList.map((file: any) => {
            if (file.status === 'done' && !file.fileUrl) {
                const { response } = file;
                // 构建上传成功后的文件对象
                const uploadedFile: UploadFileType = {
                    fileUrl: `${response?.data?.filesServer}/${response?.data?.destination}/${response?.data?.fileName}`,
                    type: file.type,
                    size: file.size,
                    uid: file.uid,
                    name: file.name,
                    fileId: response?.data?.id,
                };
                // 通知外部上传成功
                onUploadSuccessRef.current?.(uploadedFile);
                return uploadedFile;
            }
            return file;
        });

        // 更新文件列表：受控走外部 onChange，非受控更新内部状态
        if (isControlled) {
            onChangeRef.current?.(newFileList);
        } else {
            setInnerFileList(newFileList);
        }
    };

    // 删除文件（调用远程接口，失败则阻止前端移除）
    const handleRemove = async (file: UploadFileType) => {
        if (!uploadReturn || !restConfig.action) {
            antdMessage('error', '上传配置未就绪');
            return false;
        }
        try {
            await fetchApi.DELETE(
                restConfig.action,
                {
                    apiId: 'files',
                    apiExtend: 'deleteFiles',
                    apiData: { filesIds: [{ id: file.fileId }] },
                },
                undefined,
                { serviceId: uploadReturn.serviceId }
            );
            antdMessage('success', '删除成功');
            // 删除成功后需要同步列表
            const updatedList = fileList.filter((f) => f.uid !== file.uid);
            if (isControlled) {
                onChangeRef.current?.(updatedList);
            } else {
                setInnerFileList(updatedList);
            }
            return true;
        } catch (e: any) {
            antdMessage('error', `删除失败: ${e?.message || '未知错误'}`);
            return false;
        }
    };

    // 根据加载状态渲染不同 UI
    if (loading) {
        return <Button loading icon={<UploadOutlined />} disabled={disabled}>{buttonText}</Button>;
    }
    if (error) {
        return (
            <Button danger disabled={disabled} onClick={() => antdMessage('error', error)}>
                {buttonText} (配置错误)
            </Button>
        );
    }
    if (!uploadReturn) {
        return <Button disabled icon={<UploadOutlined />}>{buttonText}</Button>;
    }

    return (
        <Upload
            {...restConfig}
            fileList={fileList}
            showUploadList={showUploadList}
            onChange={handleChange}
            onRemove={handleRemove}
            disabled={disabled}
            {...restProps}
        >
            <Button icon={<UploadOutlined />} disabled={disabled}>
                {buttonText}
            </Button>
        </Upload>
    );
};

export default FileUpload;